// Requêtes des écrans des finances (TanStack Query) : toutes les clés commencent par « recettes »
// pour être invalidées ensemble après un acte ; les indicateurs REC gardent la clé « indicateurs ».
import type { ListeIndicateur, ValeurIndicateur } from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { telechargerBlob } from '../metier/csv';
import type { Anomalie, AnomalieDetail, Caisse, ClotureCaisse, CreanceDetail, CreanceReponse, PaiementDetail, PaiementReponse, Prestataire, QuittanceReponse, RecuCaisse, Redevable, ReleveDetail, ReleveResume, Remboursement, RubriqueReponse } from './donnees';

type Liste<T> = { elements: T[] };
type Parametres = Record<string, string | number | boolean | null | undefined>;

/** Clés à invalider après tout acte financier (les listes, les fiches et les tuiles). */
export const INVALIDER_RECETTES: readonly (readonly unknown[])[] = [['recettes'], ['indicateurs']];

function liste<T>(chemin: string, parametres?: Parametres) {
  return ({ signal }: { signal?: AbortSignal }) => requete<Liste<T>>(chemin, { signal, parametres }).then((r) => r.elements);
}

export function useRubriques() {
  return useQuery({ queryKey: ['recettes', 'rubriques'], queryFn: liste<RubriqueReponse>('/recettes/rubriques'), retry: false });
}

export function useRedevables(q = '') {
  return useQuery({ queryKey: ['recettes', 'redevables', q], queryFn: liste<Redevable>('/recettes/redevables', { q }), retry: false, staleTime: 30_000 });
}

export interface FiltresCreances {
  etat: string;
  echue: string;
  rubrique: string;
  nature: string;
  redevable_id?: string;
  objet_id?: string;
}

export function useCreances(f: FiltresCreances, actif = true) {
  return useQuery({ queryKey: ['recettes', 'creances', f], queryFn: liste<CreanceReponse>('/recettes/creances', { ...f }), retry: false, enabled: actif });
}

export function useCreance(id: string) {
  return useQuery({ queryKey: ['recettes', 'creance', id], queryFn: ({ signal }) => requete<CreanceDetail>(`/recettes/creances/${id}`, { signal }), retry: false, enabled: Boolean(id) });
}

export interface FiltresPaiements {
  etat: string;
  canal: string;
  en_anomalie: string;
  creance_id?: string;
}

export function usePaiements(f: FiltresPaiements, actif = true) {
  return useQuery({ queryKey: ['recettes', 'paiements', f], queryFn: liste<PaiementReponse>('/recettes/paiements', { ...f }), retry: false, enabled: actif });
}

export function usePaiement(id: string) {
  return useQuery({ queryKey: ['recettes', 'paiement', id], queryFn: ({ signal }) => requete<PaiementDetail>(`/recettes/paiements/${id}`, { signal }), retry: false, enabled: Boolean(id) });
}

export function usePrestataires(actif = true) {
  return useQuery({ queryKey: ['recettes', 'prestataires'], queryFn: liste<Prestataire>('/recettes/prestataires'), retry: false, staleTime: 5 * 60_000, enabled: actif });
}

export interface FiltresQuittances {
  etat: string;
  creance_id?: string;
  paiement_id?: string;
}

export function useQuittances(f: FiltresQuittances, actif = true) {
  return useQuery({ queryKey: ['recettes', 'quittances', f], queryFn: liste<QuittanceReponse>('/recettes/quittances', { ...f }), retry: false, enabled: actif });
}

export function useRemboursements(f: { etat: string; paiement_id?: string }) {
  return useQuery({ queryKey: ['recettes', 'remboursements', f], queryFn: liste<Remboursement>('/recettes/remboursements', { ...f }), retry: false });
}

export function useRemboursement(id: string) {
  return useQuery({ queryKey: ['recettes', 'remboursement', id], queryFn: ({ signal }) => requete<Remboursement>(`/recettes/remboursements/${id}`, { signal }), retry: false, enabled: Boolean(id) });
}

export function useCaisses() {
  return useQuery({ queryKey: ['recettes', 'caisses'], queryFn: liste<Caisse>('/recettes/caisses'), retry: false });
}

export function useRecus(caisseId: string, f: { etat?: string; date?: string } = {}) {
  return useQuery({ queryKey: ['recettes', 'recus', caisseId, f], queryFn: liste<RecuCaisse>(`/recettes/caisses/${caisseId}/recus`, f), retry: false, enabled: Boolean(caisseId) });
}

export function useClotures(caisseId: string) {
  return useQuery({ queryKey: ['recettes', 'clotures', caisseId], queryFn: liste<ClotureCaisse>(`/recettes/caisses/${caisseId}/clotures`), retry: false, enabled: Boolean(caisseId) });
}

export function useReleves() {
  return useQuery({ queryKey: ['recettes', 'releves'], queryFn: liste<ReleveResume>('/recettes/releves'), retry: false });
}

export function useReleve(id: string) {
  return useQuery({ queryKey: ['recettes', 'releve', id], queryFn: ({ signal }) => requete<ReleveDetail>(`/recettes/releves/${id}`, { signal }), retry: false, enabled: Boolean(id) });
}

export function useAnomalies(f: { etat: string; type: string; ouvertes: string }) {
  return useQuery({ queryKey: ['recettes', 'anomalies', f], queryFn: liste<Anomalie>('/recettes/anomalies', { ...f }), retry: false });
}

export function useAnomalie(id: string) {
  return useQuery({ queryKey: ['recettes', 'anomalie', id], queryFn: ({ signal }) => requete<AnomalieDetail>(`/recettes/anomalies/${id}`, { signal }), retry: false, enabled: Boolean(id) });
}

// --- Indicateurs REC ------------------------------------------------------------------------

export interface PeriodeIndicateur {
  debut: string;
  fin: string;
}

export function useValeurRec(code: string, periode: PeriodeIndicateur, actif = true) {
  return useQuery({
    queryKey: ['indicateurs', 'valeur', code, periode],
    queryFn: ({ signal }) => requete<ValeurIndicateur>('/indicateurs/valeur', { signal, parametres: { code, debut: periode.debut, fin: periode.fin } }),
    retry: false,
    enabled: actif,
  });
}

export function useListeRec(code: string, periode: PeriodeIndicateur, actif = true) {
  return useQuery({
    queryKey: ['indicateurs', 'liste', code, periode],
    queryFn: ({ signal }) => requete<ListeIndicateur>('/indicateurs/liste', { signal, parametres: { code, debut: periode.debut, fin: periode.fin, limite: 1000 } }),
    retry: false,
    enabled: actif,
  });
}

// --- Téléchargements (le jeton d'accès est en mémoire : pas de lien direct vers l'API) -----

export async function telechargerFichier(chemin: string, nom: string, parametres?: Parametres): Promise<void> {
  const r = await requete<Response>(chemin, { brut: true, parametres });
  const disposition = r.headers.get('content-disposition') ?? '';
  const nomServeur = /filename="([^"]+)"/.exec(disposition)?.[1];
  telechargerBlob(nomServeur ?? nom, await r.blob());
}
